// Engine content loader — mirrors the libraryFixtures.js dual-path design.
//
// Baked src/data/engineContent.generated.json ships with the bundle (produced
// by scripts/sync-expressions.mjs); the Firestore mirror appData/engineContent
// overrides it at runtime so authored content (domains 2–6, audio manifest
// entries, clip boundary edits) goes live WITHOUT a redeploy. Hydration
// mutates the exported object IN PLACE so module-level references stay valid;
// consumers that must react subscribe to mirrorTick, same as the fixtures.
import generated from '@/data/engineContent.generated.json';
import { bumpMirrorTick } from '@/data/mirrorCache';

function validBundle(raw) {
  return !!raw
    && Array.isArray(raw.domains) && raw.domains.length > 0
    && Array.isArray(raw.situations) && raw.situations.length > 0
    && Array.isArray(raw.expressions);
}

/** The live engine content bundle: { domains, situations, expressions }. */
export const ENGINE_CONTENT = {
  domains: validBundle(generated) ? generated.domains.slice() : [],
  situations: validBundle(generated) ? generated.situations.slice() : [],
  expressions: validBundle(generated) ? generated.expressions.slice() : [],
  generatedAt: (generated && generated.generatedAt) || null,
};

const exprById = new Map(ENGINE_CONTENT.expressions.map((e) => [e.id, e]));
const situationById = new Map(ENGINE_CONTENT.situations.map((s) => [s.id, s]));

/** Authored expression record by Airtable record id, or null. */
export function engineExpression(id) {
  return exprById.get(id) || null;
}

/** Situation record by stable situation id, or null. */
export function engineSituation(id) {
  return situationById.get(id) || null;
}

/**
 * Engine expression by LIBRARY expression uid (the 표현집 dataset's key) —
 * the bridge that lets a library card offer its masterclass clip. Rebuilt
 * lazily after hydration (uidTick guards staleness).
 */
let uidMap = null;
let uidTick = null;
export function engineExpressionByUid(uid) {
  if (!uidMap || uidTick !== ENGINE_CONTENT.generatedAt) {
    uidMap = new Map();
    uidTick = ENGINE_CONTENT.generatedAt;
    for (const e of ENGINE_CONTENT.expressions) {
      if (e.uid != null) uidMap.set(String(e.uid), e);
    }
  }
  return uidMap.get(String(uid)) || null;
}

/**
 * Replace the bundle in place with live mirror data. Rebuilds the lookups and
 * drops the scheduler's memoized situation index (it re-memoizes on next use).
 * No-op on an invalid/empty payload — the mirror is an override, never a
 * dependency.
 * @returns {boolean} hydrated?
 */
export function hydrateEngineContent(next) {
  if (!validBundle(next)) return false;
  ENGINE_CONTENT.domains.length = 0;
  ENGINE_CONTENT.situations.length = 0;
  ENGINE_CONTENT.expressions.length = 0;
  ENGINE_CONTENT.domains.push(...next.domains);
  ENGINE_CONTENT.situations.push(...next.situations);
  ENGINE_CONTENT.expressions.push(...next.expressions);
  ENGINE_CONTENT.generatedAt = next.generatedAt || null;
  delete ENGINE_CONTENT._sitById; // scheduler memo — stale after mutation
  uidMap = null; // uid bridge — rebuilt lazily from the fresh expressions
  exprById.clear();
  situationById.clear();
  for (const e of next.expressions) exprById.set(e.id, e);
  for (const s of next.situations) situationById.set(s.id, s);
  bumpMirrorTick();
  return true;
}
